/**
 * Catches a render throw anywhere below it and shows a translated message
 * with a reload button, instead of the white screen React 19 leaves behind
 * when nothing catches an error during render (constitution §I/§VI: an
 * honest failure, never a blank app). Wraps `<AppRoutes/>` in `App.tsx`
 * (PIVOT-137).
 *
 * The one class component this repo allows: `getDerivedStateFromError` has
 * no hook equivalent, so React itself requires a class here (frontend/
 * CLAUDE.md §1, docs/javascript-style.md §2's ledger — both note the
 * exception). Kept to the smallest possible class — it only tracks whether
 * something below threw — and renders the actual fallback through a
 * function component (`ErrorFallback`) so that UI still goes through
 * `useI18n()` like everywhere else in the app. `ErrorFallback` is only ever
 * mounted (and its hook only ever runs) once the class's `render` actually
 * returns it, i.e. after a real error.
 */

import { Component, type ReactNode } from 'react'
import { useI18n } from '../i18n'
import './ErrorBoundary.css'

function ErrorFallback() {
  const { t } = useI18n()
  return (
    <div className="error-boundary" role="alert">
      <div className="error-boundary-card stack">
        <p>{t('common.error')}</p>
        {/* A full reload, not a re-render: the tree that threw is gone, and
            there is nothing in this component to retry — recovering the
            actual failed screen is that screen's own job, not this one's. */}
        <button type="button" className="btn btn-primary" onClick={() => window.location.reload()}>
          {t('common.reload')}
        </button>
      </div>
    </div>
  )
}

interface ErrorBoundaryState {
  hasError: boolean
}

class ErrorBoundaryClass extends Component<{ children: ReactNode }, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true }
  }

  render(): ReactNode {
    return this.state.hasError ? <ErrorFallback /> : this.props.children
  }
}

export function ErrorBoundary({ children }: { children: ReactNode }) {
  return <ErrorBoundaryClass>{children}</ErrorBoundaryClass>
}
